import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { isOnMainProductPage } from '@theme/utilities';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    // Create a NEW AbortController each time the element is connected
    // This is important when the element is moved in the DOM (e.g., sticky bar)
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (isOnMainProductPage(this)) {
      // On main product page, only listen at document level to avoid duplicate events
      // (events bubble from section to document, causing double-firing)
      document.addEventListener(ThemeEvents.variantUpdate, this.updatePrice, { signal });
    } else {
      // In modals, dialogs, etc., listen at the container level
      const closestSection = this.closest('.shopify-section, dialog');
      closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updatePrice, { signal });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id);
    } else if (event.detail.data.productId !== this.dataset.productId) {
      // Check productId from event data (works for both bubbled and document-level events)
      return;
    }

    // Use block ID to find the correct price (handles multiple price blocks like sticky bar)
    const blockId = this.dataset.blockId;
    const selector = blockId
      ? `product-price[data-block-id="${CSS.escape(blockId)}"] [ref="priceContainer"]`
      : 'product-price [ref="priceContainer"]';
    const newPrice = event.detail.data.html?.querySelector(selector);
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
